import utils from '@/core/utils'
import _ from 'lodash'

function createSearchResultQueryParams(searchQuery, entities, activeTab = 'items') {
  const pageSizeParams = _.reduce(entities, (acc, current, key) => {
    acc[key] = current.totalCount || 200
    return acc
  }, {})
  return utils.param({
    ...pageSizeParams,
    activeTab,
    query: searchQuery
  })
}

/**
 * The item's page in Business Central, from the editable superUserBCArticleLinkTemplate, or null
 * when the template has not been filled in.
 */
function businessCentralItemUrl(template, itemNumber) {
  if (!template || !itemNumber) {
    return null
  }

  return template.replace('{itemNumber}', encodeURIComponent(itemNumber))
}

function escapeHtml(value) {
  if (value === null || value === undefined) {
    return ''
  }

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Renders a search result field as safe HTML, highlighting the ranges the backend found.
 *
 * The backend used to send this pre-wrapped in <em> tags, which meant the client had to parse
 * markup back out of a value that could carry customer-supplied text (a reference number a
 * customer typed, a company name from BC) - binding that with a bare v-html once let a customer
 * plant markup that ran for whoever looked at the same search results, typically a support agent
 * (OC-2662). Now the backend only reports numeric match positions, and the tags are built here
 * from those trusted offsets - there is no markup in the payload to parse in the first place.
 *
 * @param {string} text the (possibly backend-truncated) display text
 * @param {Array<{start: number, length: number, type: string}>} [matches]
 */
function renderHighlightedText(text, matches) {
  if (!text) {
    return ''
  }

  if (!matches || matches.length === 0) {
    return escapeHtml(text)
  }

  const sortedMatches = [...matches].sort((a, b) => a.start - b.start)

  let html = ''
  let cursor = 0

  for (const match of sortedMatches) {
    html += escapeHtml(text.slice(cursor, match.start))
    html += `<em class="instant-result__${match.type}_match">${escapeHtml(text.slice(match.start, match.start + match.length))}</em>`
    cursor = match.start + match.length
  }

  html += escapeHtml(text.slice(cursor))

  return html
}

/** Renders a `{text, matches}` highlight object (as the *Highlight DTO fields carry), or falls back to a plain field. */
function renderHighlight(highlight, fallbackText) {
  if (highlight) {
    return renderHighlightedText(highlight.text, highlight.matches)
  }

  return escapeHtml(fallbackText)
}

export { businessCentralItemUrl, createSearchResultQueryParams, escapeHtml, renderHighlightedText, renderHighlight }
